import { useLayoutEffect, useRef } from 'react'
import classNames from 'classnames'

import { ICON_HEIGHT_40, IconSpinner } from '../../Icon'
import { UPLOAD_IMAGE_CROP_CORNERS } from '../UploadImage.constants'

import {
  getUploadImageStageCropStyle,
  getUploadImageStageImageHostStyle,
  getUploadImageStageMaskStyle,
} from './UploadImageStage.utils'

import * as styles from './UploadImageStage.module.css'

function UploadImageStage({
  crop,
  cropShape,
  dragCursor,
  hasImage,
  image,
  isDisabled,
  isDragging,
  layout,
  rotation,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}) {
  const imageHostRef = useRef(null)

  // Mounts the exact <img> the engine already decoded — no second decode/flash.
  useLayoutEffect(() => {
    const host = imageHostRef.current

    if (host && image) {
      host.appendChild(image)

      return () => {
        if (image.parentNode === host) {
          host.removeChild(image)
        }
      }
    }
  }, [image])

  return !hasImage ? (
    <div className={styles.stage}>
      <IconSpinner className={styles.spinner} size={ICON_HEIGHT_40} />
    </div>
  ) : (
    <div
      className={classNames(
        styles.stage,
        isDragging && styles.dragging,
        isDisabled && styles.disabled
      )}
      style={dragCursor && !isDisabled ? { cursor: dragCursor } : undefined}
      onPointerCancel={isDisabled ? undefined : onPointerUp}
      onPointerDown={isDisabled ? undefined : onPointerDown}
      onPointerMove={isDisabled ? undefined : onPointerMove}
      onPointerUp={isDisabled ? undefined : onPointerUp}
    >
      <div
        className={styles.imageHost}
        ref={imageHostRef}
        style={getUploadImageStageImageHostStyle({ layout, rotation })}
      />
      <div
        className={styles.mask}
        style={getUploadImageStageMaskStyle({ crop, cropShape })}
      />
      {isDragging ? (
        <div
          className={styles.grid}
          style={getUploadImageStageCropStyle(crop)}
        />
      ) : null}
      <div
        className={classNames(styles.frame, isDragging && styles.frameActive)}
        data-role='frame'
        style={getUploadImageStageCropStyle(crop)}
      >
        {UPLOAD_IMAGE_CROP_CORNERS.map(corner => (
          <span
            className={classNames(styles.handle, styles[corner])}
            data-corner={corner}
            key={corner}
          />
        ))}
      </div>
    </div>
  )
}

export default UploadImageStage
